import { AutoSyncConfig, FormTemplate, ProjectConfig } from "db/definitions";
import { getAllFormTemplates } from "db/queries/formTemplateQueries";
import { useEffect, useState } from "react";
import { syncSelectedTemplatesOffline } from "utils/templateSyncModules";
import { v4 as uuid } from "uuid";
import { bulkDeleteAutoSyncConfigs, bulkSaveAutoSyncConfigs, getAllAutoSyncConfigs } from "../db/queries/autoSyncConfigQueries";

function useAutoSync(projectConfigs: ProjectConfig[]) {
	const [autoSyncConfigs, setAutoSyncConfigs] = useState<Record<string, AutoSyncConfig>>({});
	const [projectConfigMap, setProjectConfigMap] = useState<Record<string, ProjectConfig>>({});

	/**
	 * This is to grab and setup the autosyncconfigs from the local indexedDb and store in state.
	 */
	useEffect(() => {
		async function retrieveAutoSyncConfigs(): Promise<void> {
			const map: Record<string, AutoSyncConfig> = {};
			const retrievedConfigs = await getAllAutoSyncConfigs();
			// configs come in as an array, at this point they will be mapped out.
			for (let i = 0; i < retrievedConfigs.length; i++) {
				const retrievedConfig = retrievedConfigs[i];
				// map em out by the base template prop, which will determine like types
				map[retrievedConfig.baseTemplate] = retrievedConfig;
			}
			setAutoSyncConfigs(map);
		}

		retrieveAutoSyncConfigs();
	}, []);

	useEffect(() => {
		function mapProjectConfigs(projectConfigsToUse: ProjectConfig[]): void {
			const newProjectConfigMap: Record<string, ProjectConfig> = {};
			for (let i = 0; i < projectConfigsToUse.length; i++) {
				const projectConfig = projectConfigsToUse[i];
				newProjectConfigMap[projectConfig.projectId] = projectConfig;
			}
			setProjectConfigMap(newProjectConfigMap);
		}

		mapProjectConfigs(projectConfigs);
	}, [projectConfigs]);

	/**
	 * Adds templates to the autosync config, so they will be updated.
	 * @param templates that need to be auto synced.  We just need the ver
	 */
	async function addTemplatesToAutoSync(templates: FormTemplate[]): Promise<Record<string, AutoSyncConfig>> {
		// first find the auto sync that has the matching baseTemplateId.  This could
		// also be the id, in which case it is the default template.
		const autoSyncConfigsCopy = { ...autoSyncConfigs };
		const newConfigsMap: Record<string, AutoSyncConfig> = {};
		for (let i = 0; i < templates.length; i++) {
			const templateToAdd = templates[i];
			const { id, template } = templateToAdd;
			if (autoSyncConfigsCopy[template]) {
				// there is an auto sync config already so just need to add the template in
				const autoSyncConfigToEdit = autoSyncConfigsCopy[template];
				if(!autoSyncConfigToEdit.versionIds.includes(id)) {
					autoSyncConfigToEdit.versionIds.push(templateToAdd.id);
					autoSyncConfigsCopy[template] = autoSyncConfigToEdit;
					newConfigsMap[autoSyncConfigToEdit.id] = autoSyncConfigToEdit;
				}
				// overwrite or add it to the map that will put the docs in the db
			} else {
				// REFACTOR NOTE: the below used to call .toISOString() on Date.now() but that throughs an error, so I refactored it to only be the date, this still seems to be working but I wanted to leave a note incase it is important somewhere else.
				const time = new Date().toISOString();
				// new one need to create the autoSync doc then add to the indexedDb
				// followed by adding to state.
				const newAutoSyncObj: AutoSyncConfig = {
					id: uuid(),
					baseTemplate: template,
					versionIds: [id],
					createdAt: time,
					updatedAt: time,
					autoSync: true,
				};

				// the auto sync configs will be mapped out by their id, to make sure
				// it is staying up to date amongst several templates that could be using
				// the same one.
				newConfigsMap[newAutoSyncObj.id] = newAutoSyncObj;
				// newConfigs.push(newAutoSyncObj);
				autoSyncConfigsCopy[template] = newAutoSyncObj;
			}
		}

		const configsToPut = Object.values(newConfigsMap);
		await bulkSaveAutoSyncConfigs(configsToPut);
		setAutoSyncConfigs(autoSyncConfigsCopy);
		return autoSyncConfigsCopy;
	}

	async function removeTemplatesFromAutoSync(templatesToRemove: FormTemplate[]) {
		const autoSyncToDelete = new Set<string>();
		const autoSyncConfigsCopy = { ...autoSyncConfigs };
		// get the matching autoSync
		for (let i = 0; i < templatesToRemove.length; i++) {
			const templateToRemove = templatesToRemove[i];
			const matchingAutoSyncConfig = autoSyncConfigsCopy[templateToRemove.template];

			if (matchingAutoSyncConfig) {
				const filteredVersions = matchingAutoSyncConfig.versionIds.filter((versionId) => versionId !== templateToRemove.id);
				if (filteredVersions.length < 1) {
					// its the last one
					delete autoSyncConfigsCopy[matchingAutoSyncConfig.baseTemplate];
					autoSyncToDelete.add(matchingAutoSyncConfig.id);
				} else {
					matchingAutoSyncConfig.versionIds = filteredVersions;
					// if its not the last, update the one in the state obj
					autoSyncConfigsCopy[templateToRemove.template] = matchingAutoSyncConfig;
				}
			} else {
				console.warn("No config to remove for this template! ", templateToRemove);
			}
		}
		await bulkDeleteAutoSyncConfigs(Array.from(autoSyncToDelete));
		setAutoSyncConfigs(autoSyncConfigsCopy);
		return autoSyncConfigsCopy;
	}

	/**
	 * This handles the logic to determine if a template needs to be updated
	 * it will handle all of the cloud templates passed in and all of the autoSyncConfigs
	 * @param cloudTemplates
	 */
	function handleAutoUpdate(cloudTemplates: FormTemplate[]): Promise<void> {
		// CLOUD TEMPLATE WILL BE THE NEW SLIMMED DOWN VERSIONS

		const templatesToAutoUpdate = [];
		const autoSyncConfigsCopy = { ...autoSyncConfigs };
		// First things is to organize out what is even a possible one to be auto downloaded.
		// Any template that might need to
		for (let i = 0; i < cloudTemplates.length; i++) {
			const cloudTemplate = cloudTemplates[i];
			// project side of things
			if (cloudTemplate.projectId) {
				// its a project template so check if its got a projectConfig, if not then skip
				if (projectConfigMap[cloudTemplate.projectId]) {
					// its got a projectConfig so must be autoSynced.  These are separate from AutSyncConfigs for now
					templatesToAutoUpdate.push(cloudTemplate);
				}
			} else if (autoSyncConfigsCopy[cloudTemplate.template]) {
				// its an autoSync one so yeah gotta do the thing
				// check if this specific one is in the auto update
				const config = autoSyncConfigs[cloudTemplate.template];

				const indexOfTemplate = config.versionIds.indexOf(cloudTemplate.id);
				if (indexOfTemplate > -1) {
					// the template needs to be auto updated.
					templatesToAutoUpdate.push(cloudTemplate);
				} else {
					// the template is not in the auto sync yet...
					// that means it is a type that has a new version that should be pulled in
					// AT THIS POINT it will handle anything passed in
					// Need to update the autoSync config and push the template in templatestoAutupdate
					autoSyncConfigsCopy[cloudTemplate.template].versionIds.push(cloudTemplate.id);
					templatesToAutoUpdate.push(cloudTemplate);
				}
			}
		}

		// The data that needs to be checked then created:
		// now loop through the templates and determine which ones actually need to be updated.
		// then bulk put those
		// update the autosync configs in the db and in state
		return updateTemplates(templatesToAutoUpdate);
	}

	/**
	 * This handles determining if the provided templates offline are older than the
	 * ones passed in, and if so, updates or creates them.
	 */
	async function updateTemplates(templatesToPotentiallyUpdate: FormTemplate[]): Promise<void> {
		const templatesThatMightUpdate: Record<string, FormTemplate> = {};
		// should probal put these in a map then loop through the retrievedOfflineTemplates.
		for (let i = 0; i < templatesToPotentiallyUpdate.length; i++) {
			const template = templatesToPotentiallyUpdate[i];
			templatesThatMightUpdate[template.id] = template;
		}
		const retrievedOfflineTemplates = await getAllFormTemplates();
		// check if the id exists in the offline templates, if it does, then check the last updated at.
		// if the updated at is not newer then the form template must have been edited last while offline
		for (let i = 0; i < retrievedOfflineTemplates.length; i++) {
			const offlineTemplate = retrievedOfflineTemplates[i];
			if (templatesThatMightUpdate[offlineTemplate.id]) {
				// its an update. so just need to check if the updated at is newer.
				const potentialUpdate = templatesThatMightUpdate[offlineTemplate.id];
				if (potentialUpdate.updatedAt <= offlineTemplate.updatedAt) {
					// so basically the offline one is the correct one so we can just delete the key from the
					// map since this is the same template, and we have the most recent, so will not need
					// to download this version.  This is pretty much the only situation we dont need
					// to download the template.
					delete templatesThatMightUpdate[offlineTemplate.id];
				}
			}
		}

		const templatesToDownload = Object.values(templatesThatMightUpdate);

		// Need to loop through and grab the full templates for each one
		if (templatesToDownload.length > 0) {
			await syncSelectedTemplatesOffline(templatesToDownload);
		}
	}

	const results = {
		autoSyncConfigs,
		addTemplatesToAutoSync,
		removeTemplatesFromAutoSync,
		handleAutoUpdate,
	};

	return results;
}

export default useAutoSync;
